import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Zap, Shield, Wallet, AlertCircle, RefreshCw, LogIn, AlertTriangle, CheckCircle,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Target, Activity,
  Crosshair, BarChart3, Clock, DollarSign, Wifi, WifiOff, ChevronRight, X, Calculator } from 'lucide-react';
import { toast } from 'sonner';

// Helper for generating idempotency keys
const generateIdempotencyKey = () => Math.random().toString(36).substring(2) + Date.now().toString(36);

export function ConnectDhanModal({ open, onOpenChange, onConnected }) {
  const [clientId, setClientId] = useState(() => localStorage.getItem('dhan_client_id') || '');
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    if (!clientId || !accessToken) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/trading/connect-dhan`, {
        client_id: clientId,
        access_token: accessToken
      }, { withCredentials: true });

      localStorage.setItem('dhan_client_id', clientId);
      toast.success(res.data.message || 'Dhan connected securely!');
      if (onConnected) onConnected();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect Dhan');
    } finally {
      setLoading(false);
    }
  }


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-gray-50 dark:bg-[#0A0E17] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-outfit">
            <LogIn className="w-5 h-5 text-[#00F0FF]" />
            Connect Dhan Account
          </DialogTitle>
          <DialogDescription className="text-gray-500 dark:text-[#94A3B8] font-plex text-xs">
            Enter your Client ID and Access Token. Tokens are securely encrypted and verified instantly.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">

          <div className="grid gap-2">
            <Label htmlFor="clientId" className="text-gray-500 dark:text-[#94A3B8] text-xs">Client ID</Label>
            <Input id="clientId" value={clientId} onChange={e => setClientId(e.target.value)} className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white focus-visible:ring-[#00F0FF]" placeholder="e.g. 1100012345" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="accessToken" className="text-gray-500 dark:text-[#94A3B8] text-xs">Access Token</Label>
            <Input id="accessToken" type="password" value={accessToken} onChange={e => setAccessToken(e.target.value)} className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white focus-visible:ring-[#00F0FF]" placeholder="Paste your Dhan JWT token here..." />
          </div>
        </div>
        <Button onClick={handleConnect} disabled={loading} className="w-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold font-outfit">
          {loading ? 'Verifying & Connecting...' : 'Connect & Authorize'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function TradeExecutionModal({ open, onOpenChange, alert, livePrice: initialLivePrice }) {
  const [status, setStatus] = useState(null);
  const [securityId, setSecurityId] = useState('');
  const [resolvedSymbol, setResolvedSymbol] = useState('');
  const [securityResults, setSecurityResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [orderType, setOrderType] = useState('LIMIT');
  const [qtyData, setQtyData] = useState(null);
  const [orderResult, setOrderResult] = useState(null);
  const [liveData, setLiveData] = useState(initialLivePrice || null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const idempotencyRef = useRef(generateIdempotencyKey());
  const liveIntervalRef = useRef(null);

  const isDemand = alert?.zone_type === 'DEMAND';
  const accentColor = isDemand ? '#00FF88' : '#FF3B30';
  const accentBg = isDemand ? 'rgba(0,255,136,0.06)' : 'rgba(255,59,48,0.06)';

  const cmp = liveData?.last_price || 0;
  const entry = orderType === 'MARKET' ? (cmp || alert?.entry) : alert?.entry;
  const sl = alert?.stop_loss;
  const target = alert?.target;

  // R:R calculation
  const rr = (() => {
    if (!entry || !sl || !target) return '—';
    const risk = Math.abs(entry - sl);
    const reward = Math.abs(target - entry);
    return risk > 0 ? (reward / risk).toFixed(1) : '—';
  })();

  // % from entry
  const distFromEntry = cmp && entry ? ((cmp - entry) / entry * 100).toFixed(2) : null;
  // % to target
  const distToTarget = cmp && target ? ((target - cmp) / cmp * 100).toFixed(2) : null;
  // % to SL
  const distToSl = cmp && sl ? ((cmp - sl) / cmp * 100).toFixed(2) : null;

  // Progress: where is CMP between SL → Entry → Target?
  const progressPct = (() => {
    if (!cmp || !sl || !target) return 0;
    const low = isDemand ? sl : target;
    const high = isDemand ? target : sl;
    const range = high - low;
    if (range <= 0) return 50;
    return Math.max(0, Math.min(100, ((cmp - low) / range) * 100));
  })();

  // Fetch live price for the alert symbol
  const fetchLivePrice = useCallback(async () => {
    if (!alert?.script_name) return;
    setLiveLoading(true);
    try {
      const res = await axios.get(`${API}/market/ltp?symbols=${alert.script_name}`);
      const data = res.data?.data?.[alert.script_name];
      if (data) setLiveData(data);
    } catch (e) { /* silent */ }
    finally { setLiveLoading(false); }
  }, [alert?.script_name]);

  useEffect(() => {
    if (open) {
      setOrderResult(null);
      setLiveData(initialLivePrice || null);
      setSecurityId('');
      setResolvedSymbol('');
      setSecurityResults([]);
      idempotencyRef.current = generateIdempotencyKey();
      fetchStatusAndSecurity();
      fetchLivePrice();
      // Poll live price every 8s while modal is open
      liveIntervalRef.current = setInterval(fetchLivePrice, 8000);
    } else {
      if (liveIntervalRef.current) clearInterval(liveIntervalRef.current);
    }
    return () => { if (liveIntervalRef.current) clearInterval(liveIntervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, alert]);

  useEffect(() => {
    if (open && entry && sl) calculateSafeQuantity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry, sl, status]);

  const fetchStatusAndSecurity = async () => {
    setLoading(true);
    try {
      const statusRes = await axios.get(`${API}/trading/dhan-status`, { withCredentials: true }).catch(() => null);
      if (statusRes?.data) setStatus(statusRes.data);

      if (alert?.script_name) {
        const secRes = await axios.get(`${API}/trading/resolve-security?symbol=${alert.script_name}`, { withCredentials: true }).catch(() => null);
        const results = secRes?.data?.results || [];
        setSecurityResults(results);
        if (results.length === 1) {
          setSecurityId(results[0].security_id || results[0].symbol);
          setResolvedSymbol(results[0].symbol);
        } else if (results.length > 1) {
          // Try to auto-select exact symbol match first
          const exact = results.find(r =>
            r.symbol?.toUpperCase() === alert.script_name?.toUpperCase()
          );
          if (exact) {
            setSecurityId(exact.security_id || exact.symbol);
            setResolvedSymbol(exact.symbol);
          } else {
            // No exact match — let user pick from dropdown
            setSecurityId('');
            setResolvedSymbol('');
          }
        } else {
          setSecurityId('UNRESOLVED');
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const calculateSafeQuantity = async () => {
    try {
      const res = await axios.post(`${API}/trading/calculate-qty`, {
        symbol: alert?.script_name,
        entry: parseFloat(entry),
        stop_loss: parseFloat(sl)
      }, { withCredentials: true });
      setQtyData(res.data);
    } catch (err) { console.error('Failed to calculate qty', err); }
  };

  const getDisabledReason = () => {
    // Don't block on loading — button is already disabled={loading} below
    if (loading) return null;
    if (!status?.connected) return 'Broker Not Connected';
    if (status?.expired) return 'Broker Token Expired';
    if (!securityId || securityId === 'UNRESOLVED') return 'Symbol Unresolved';
    if (qtyData && (qtyData.available_capital === undefined || qtyData.available_capital <= 0)) return 'Insufficient Funds in Dhan';
    if (!qtyData?.final_safe_qty || qtyData.final_safe_qty <= 0) return 'Safe Qty is Zero — Insufficient Capital';
    if (!sl || !target) return 'Missing Stop Loss or Target';
    return null;
  };

  const handle1ClickOrder = async () => {
    const reason = getDisabledReason();
    if (reason) { toast.error(reason); return; }
    setExecuting(true);
    try {
      // Step 1: Preflight checks
      await axios.post(`${API}/trading/preflight-order`, {
        symbol: resolvedSymbol, security_id: securityId,
        exchange_segment: 'NSE_EQ',
        transaction_type: isDemand ? 'BUY' : 'SELL',
        order_type: orderType, quantity: qtyData.final_safe_qty,
        price: entry, stop_loss: sl, target
      }, { withCredentials: true });

      // Step 2: Instant Execution
      const res = await axios.post(`${API}/trading/place-order`, {
        symbol: resolvedSymbol, security_id: securityId,
        exchange_segment: alert?.exchange || 'NSE_EQ',
        transaction_type: isDemand ? 'BUY' : 'SELL',
        quantity: qtyData.final_safe_qty,
        order_type: orderType, product_type: 'CNC',
        price: parseFloat(entry), target: parseFloat(target),
        stop_loss: parseFloat(sl), mode: 'LIVE',
        idempotency_key: idempotencyRef.current
      }, { withCredentials: true });

      setOrderResult({ success: true, ...res.data });
    } catch (err) {
      let errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Execution failed';
      if (typeof errorMsg === 'object') {
        errorMsg = errorMsg.error_message || errorMsg.remarks || JSON.stringify(errorMsg);
      }
      setOrderResult({ success: false, error: String(errorMsg) });
      toast.error(String(errorMsg));
    } finally { setExecuting(false); }
  };

  if (!alert) return null;

  // ─── ORDER SUCCESS SCREEN ─────────────────────────────────────────────────
  if (orderResult) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[420px] bg-[#0A0E17] border-[#1E293B] text-white text-center p-6 shadow-2xl">
          {orderResult.success ? (
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-full bg-[#00FF88]/10 border-2 border-[#00FF88]/40 flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(0,255,136,0.2)]">
                <CheckCircle className="w-8 h-8 text-[#00FF88]" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-outfit text-[#00FF88]">Order Placed Live!</h2>
                <p className="text-[#94A3B8] text-xs mt-0.5">Execution transmitted to Dhan Broker</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3 text-left space-y-2 text-xs font-mono">
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Order ID</span>
                  <span className="text-white font-bold">{orderResult.order_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Symbol</span>
                  <span className="font-bold text-white">{alert.script_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Action</span>
                  <span className={`font-bold ${isDemand ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>{isDemand ? 'BUY' : 'SELL'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Mode</span>
                  <span className="text-emerald-400 font-bold">LIVE CNC</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-white/5">
                  <span className="text-[#94A3B8]">Journal</span>
                  <span className="text-[#00FF88] font-semibold flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Auto-Logged
                  </span>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={() => { onOpenChange(false); window.location.href = '/journal'; }} variant="outline" className="flex-1 border-white/20 hover:bg-white/5 font-semibold text-xs h-9">
                  View Journal
                </Button>
                <Button onClick={() => onOpenChange(false)} className="flex-1 bg-[#00FF88] text-black font-bold h-9 hover:brightness-110 text-xs">
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-full bg-[#FF3B30]/10 border-2 border-[#FF3B30]/40 flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-[#FF3B30]" />
              </div>
              <h2 className="text-lg font-bold font-outfit text-[#FF3B30]">Order Failed</h2>
              <p className="text-[#94A3B8] text-xs bg-white/5 p-3 rounded-lg leading-relaxed">{orderResult.error}</p>
              <Button onClick={() => setOrderResult(null)} variant="outline" className="w-full border-white/20 hover:bg-white/5 text-xs h-9">Try Again</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // ─── MAIN COMPACT HIGH-DENSITY TRADE SETUP SCREEN (ZERO SCROLL) ───────────
  const disabledReason = getDisabledReason();
  const isMarketOpen = liveData?.market_open;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[620px] w-[95vw] bg-[#0A0E17] border border-[#1E293B] text-white p-0 rounded-2xl overflow-hidden shadow-2xl">
          {/* Top Brand Accent Stripe */}
          <div className="h-1 w-full" style={{ background: accentColor }} />

          {/* ── UNIFIED COMPACT HEADER & HERO ────────────────────────────────── */}
          <div className="px-4 py-3 border-b border-white/[0.06] bg-gradient-to-b from-white/[0.03] to-transparent relative">
            <div className="flex items-center justify-between gap-2">
              {/* Left: Direction, Symbol & Tags */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 font-outfit font-black text-lg sm:text-xl tracking-wide" style={{ color: accentColor }}>
                  {isDemand ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                  <span>{isDemand ? 'BUY' : 'SELL'} {alert.script_name}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-mono">
                  {alert.setup_type && (
                    <span className="text-[#FFCC00] bg-[#FFCC00]/10 px-1.5 py-0.5 rounded font-semibold border border-[#FFCC00]/20">
                      {alert.setup_type}
                    </span>
                  )}
                  {alert.timeframe && (
                    <span className="text-gray-300 bg-white/5 px-1.5 py-0.5 rounded border border-white/10">
                      ⏱ {alert.timeframe}
                    </span>
                  )}
                  {alert.score && (
                    <span className="text-[#00FF88] bg-[#00FF88]/10 px-1.5 py-0.5 rounded border border-[#00FF88]/20 font-bold">
                      ★ {alert.score}
                    </span>
                  )}
                  {securityId && securityId !== 'UNRESOLVED' && (
                    <span className="text-gray-400 bg-white/5 px-1.5 py-0.5 rounded border border-white/10 hidden sm:inline-block">
                      ID: {securityId}
                    </span>
                  )}
                </div>
              </div>

              {/* Right: Broker Connection Pill + Close */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold ${
                  status?.connected && !status?.expired
                    ? 'bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20'
                    : 'bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20'
                }`}>
                  {status?.connected && !status?.expired ? (
                    <><span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" />DHAN LIVE</>
                  ) : (
                    <><AlertCircle className="w-3 h-3" />{status?.expired ? 'EXPIRED' : 'NOT CONNECTED'}</>
                  )}
                </div>
                <button
                  onClick={() => onOpenChange(false)}
                  className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* ── CMP & FUNDS TICKER ROW ────────────────────────────────────── */}
            <div className="mt-2.5 pt-2 border-t border-white/[0.04] flex items-center justify-between flex-wrap gap-2 text-xs font-mono">
              {/* CMP Live Display */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-gray-400">
                  {isMarketOpen ? (
                    <><span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" /><span className="text-[#00FF88]">LTP</span></>
                  ) : (
                    <><span className="w-1.5 h-1.5 rounded-full bg-gray-500" /><span>LTP</span></>
                  )}
                </div>
                <span className="text-base sm:text-lg font-black text-white">
                  ₹{cmp > 0 ? Number(cmp).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                </span>
                {liveData?.change != null && (
                  <span className={`text-[11px] font-bold ${liveData.change >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                    {liveData.change >= 0 ? '▲' : '▼'}{Math.abs(liveData.change).toFixed(2)} ({liveData.change_pct >= 0 ? '+' : ''}{liveData.change_pct}%)
                  </span>
                )}
                <button
                  onClick={fetchLivePrice}
                  title="Refresh Live Price"
                  className="p-0.5 text-gray-400 hover:text-white transition-colors"
                >
                  <RefreshCw className={`w-3 h-3 ${liveLoading ? 'animate-spin text-[#00F0FF]' : ''}`} />
                </button>
              </div>

              {/* Funds & R:R Summary */}
              <div className="flex items-center gap-3 text-[11px]">
                <div className="flex items-center gap-1 text-gray-400">
                  <Wallet className="w-3 h-3 text-[#00F0FF]" />
                  <span>Funds:</span>
                  <span className="font-bold text-white">
                    ₹{Number(qtyData?.available_capital || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="hidden sm:flex items-center gap-1 text-gray-400">
                  <Target className="w-3 h-3 text-[#00FF88]" />
                  <span>R:R:</span>
                  <span className="font-bold text-[#00FF88]">1:{rr}</span>
                </div>
              </div>
            </div>

            {/* ── COMPACT CMP HORIZON PROGRESS TRACK ──────────────────────────── */}
            {cmp > 0 && sl && target && (
              <div className="mt-2 pt-1">
                <div className="relative h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                  <div className="absolute left-0 top-0 h-full rounded-l-full bg-[#FF3B30]/50" style={{ width: isDemand ? '18%' : '82%' }} />
                  <div className="absolute right-0 top-0 h-full rounded-r-full bg-[#00FF88]/50" style={{ width: isDemand ? '18%' : '18%' }} />
                  <div
                    className="absolute top-0 h-full w-1.5 rounded-full bg-white shadow-[0_0_6px_white] transition-all duration-300"
                    style={{ left: `${progressPct}%`, transform: 'translateX(-50%)' }}
                  />
                </div>
                <div className="flex justify-between items-center mt-1 text-[9px] font-mono text-gray-400">
                  <span className="text-[#FF3B30]">SL ₹{Number(isDemand ? sl : target).toLocaleString('en-IN')}</span>
                  <span className="text-gray-300">CMP @ {progressPct.toFixed(0)}%</span>
                  <span className="text-[#00FF88]">Target ₹{Number(isDemand ? target : sl).toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}
          </div>

          {/* ── 2-COLUMN HIGH-DENSITY DASHBOARD BODY ──────────────────────────── */}
          <div className="p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 gap-3 bg-[#0A0E17] max-h-[60vh] overflow-y-auto overscroll-contain touch-pan-y custom-scrollbar overscroll-contain touch-pan-y">
            {/* ── LEFT COLUMN: TRADE LEVELS & ORDER SETTINGS ────────────────── */}
            <div className="space-y-2.5">
              {/* Price Levels 3-Grid */}
              <div>
                <div className="flex items-center justify-between mb-1 text-[10px] font-mono uppercase tracking-wider text-gray-400">
                  <span>Trade Levels</span>
                  <span className="text-[#00F0FF]">Setup Config</span>
                </div>
                <div className="grid grid-cols-3 gap-1.5 text-center font-mono">
                  {/* Entry */}
                  <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-2 hover:border-white/20 transition-colors">
                    <p className="text-[9px] uppercase text-gray-400">Entry</p>
                    <p className="text-sm font-bold text-white mt-0.5">
                      ₹{entry ? Number(entry).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}
                    </p>
                    <p className="text-[8px] text-gray-400 mt-0.5 truncate">
                      {distFromEntry != null ? `${parseFloat(distFromEntry) >= 0 ? '+' : ''}${distFromEntry}%` : (orderType === 'MARKET' ? 'Market' : 'Limit')}
                    </p>
                  </div>
                  {/* Stop Loss */}
                  <div className="bg-[#FF3B30]/[0.05] border border-[#FF3B30]/20 rounded-xl p-2">
                    <p className="text-[9px] uppercase text-[#FF3B30]">Stop Loss</p>
                    <p className="text-sm font-bold text-[#FF3B30] mt-0.5">
                      ₹{sl ? Number(sl).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}
                    </p>
                    <p className="text-[8px] text-[#FF3B30]/80 mt-0.5 truncate">
                      {distToSl != null ? `${distToSl}%` : '—'}
                    </p>
                  </div>
                  {/* Target */}
                  <div className="bg-[#00FF88]/[0.05] border border-[#00FF88]/20 rounded-xl p-2">
                    <p className="text-[9px] uppercase text-[#00FF88]">Target</p>
                    <p className="text-sm font-bold text-[#00FF88] mt-0.5">
                      ₹{target ? Number(target).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}
                    </p>
                    <p className="text-[8px] text-[#00FF88]/80 mt-0.5 truncate">
                      {distToTarget != null ? `+${distToTarget}%` : '—'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Order Controls: Type & Mode */}
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-2.5 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[9px] font-mono text-gray-400 uppercase block mb-1">Order Type</label>
                    <div className="flex bg-black/40 p-0.5 rounded-lg border border-white/10">
                      {['LIMIT', 'MARKET'].map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setOrderType(type)}
                          className={`flex-1 py-1 rounded text-[10px] font-bold font-mono transition-all ${
                            orderType === type
                              ? 'bg-white/20 text-white shadow-sm'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          style={orderType === type ? { backgroundColor: accentColor, color: isDemand ? '#000' : '#fff' } : {}}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[9px] font-mono text-gray-400 uppercase block mb-1">Execution</label>
                    <div className="flex items-center justify-between px-2.5 py-1 bg-black/40 border border-white/10 rounded-lg text-[10px] font-mono font-bold text-red-400">
                      <span>LIVE CNC</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                    </div>
                  </div>
                </div>

                {/* Instrument match if multiple results */}
                {securityResults.length > 1 && (
                  <div>
                    <label className="text-[9px] font-mono text-gray-400 uppercase block mb-1">Select Instrument</label>
                    <select
                      value={securityId}
                      onChange={e => {
                        setSecurityId(e.target.value);
                        const match = securityResults.find(s => s.security_id === e.target.value);
                        if (match) setResolvedSymbol(match.symbol);
                      }}
                      className="w-full bg-[#12121A] border border-white/10 text-white rounded-lg p-1.5 text-xs font-mono"
                    >
                      <option value="" disabled>Select match...</option>
                      {securityResults.map(res => (
                        <option key={res.security_id} value={res.security_id}>{res.symbol}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* ── RIGHT COLUMN: POSITION SIZING & RISK MANAGEMENT ─────────── */}
            <div className="space-y-2.5">
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-2.5 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono text-[#00F0FF] font-bold uppercase">
                    <Shield className="w-3.5 h-3.5 text-[#00F0FF]" />
                    <span>Safe 1% Risk Sizing</span>
                  </div>
                  <span className="text-[9px] font-mono text-gray-400">Dhan Auto-Guard</span>
                </div>

                {/* Sizing Stats 3-Grid */}
                {loading ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="w-4 h-4 animate-spin text-[#00F0FF]" />
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5 text-center font-mono">
                    <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-1.5">
                      <p className="text-[8px] uppercase text-gray-400">Qty (Shares)</p>
                      <p className="text-base font-black text-[#00F0FF] mt-0.5">{qtyData?.final_safe_qty || 0}</p>
                    </div>
                    <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-1.5">
                      <p className="text-[8px] uppercase text-gray-400">Max Risk</p>
                      <p className="text-xs font-bold text-[#FF3B30] mt-1">
                        ₹{(qtyData?.estimated_risk_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-1.5">
                      <p className="text-[8px] uppercase text-gray-400">Order Value</p>
                      <p className="text-xs font-bold text-white mt-1">
                        ₹{(qtyData?.estimated_order_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                  </div>
                )}

                {/* Projected Outcomes Mini Bar */}
                {qtyData?.final_safe_qty > 0 && entry && target && sl && (
                  <div className="pt-2 border-t border-white/[0.06] flex items-center justify-between text-[10px] font-mono">
                    <div className="text-left">
                      <span className="text-gray-400 text-[8px] uppercase block">If SL Hit</span>
                      <span className="text-[#FF3B30] font-bold">
                        -₹{(Math.abs(entry - sl) * qtyData.final_safe_qty).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div className="flex-1 mx-2 flex items-center gap-1">
                      <div className="flex-1 h-0.5 bg-[#FF3B30]/40 rounded-full" />
                      <span className="text-[9px] text-gray-500 font-bold">P&L</span>
                      <div className="flex-1 h-0.5 bg-[#00FF88]/40 rounded-full" />
                    </div>
                    <div className="text-right">
                      <span className="text-gray-400 text-[8px] uppercase block">If Target Hit</span>
                      <span className="text-[#00FF88] font-bold">
                        +₹{(Math.abs(target - entry) * qtyData.final_safe_qty).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Admin Remark / Insight (Compact Pill) */}
              {alert.admin_remark && (
                <div className="p-2 bg-[#FFCC00]/[0.05] border border-[#FFCC00]/15 rounded-xl flex items-start gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-[#FFCC00] shrink-0 mt-0.5" />
                  <div className="text-[10px] font-sans text-[#CBD5E1] leading-tight">
                    <span className="text-[#FFCC00] font-bold font-mono uppercase mr-1">Admin Insight:</span>
                    {alert.admin_remark}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── COMPACT ACTION FOOTER ─────────────────────────────────────────── */}
          <div className="p-3 border-t border-white/[0.06] bg-[#070A10] flex items-center gap-2">
            {!status?.connected || status?.expired ? (
              <div className="w-full flex items-center justify-between gap-2 bg-[#FF3B30]/10 border border-[#FF3B30]/25 rounded-xl p-2">
                <div className="flex items-center gap-2 text-xs text-[#FF3B30] font-medium font-sans">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{status?.expired ? 'Broker Token Expired' : 'Dhan Broker Not Connected'}</span>
                </div>
                <Button
                  size="sm"
                  onClick={() => setConnectModalOpen(true)}
                  className="bg-[#00F0FF] text-black font-bold text-xs h-7 px-3 hover:bg-[#00F0FF]/90 font-outfit shadow-md shrink-0"
                >
                  <LogIn className="w-3.5 h-3.5 mr-1" /> Connect Dhan
                </Button>
              </div>
            ) : disabledReason ? (
              <div className="w-full flex items-center justify-between gap-2 bg-white/5 border border-white/10 rounded-xl p-2 text-xs text-gray-300 font-mono">
                <div className="flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>{disabledReason}</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-400 hover:text-white text-xs h-7">
                  Dismiss
                </Button>
              </div>
            ) : (
              <div className="w-full flex items-center gap-2">
                <Button
                  onClick={handle1ClickOrder}
                  disabled={executing || loading}
                  className="flex-1 h-10 font-bold font-outfit text-sm rounded-xl relative overflow-hidden group shadow-lg transition-all transform hover:brightness-110"
                  style={{ background: accentColor, color: isDemand ? '#000' : '#fff' }}
                >
                  {executing ? (
                    <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    <span className="flex items-center justify-center gap-1.5">
                      <Zap className="w-4 h-4" />
                      1-CLICK EXECUTE {isDemand ? 'BUY' : 'SELL'}
                      {qtyData?.final_safe_qty > 0 && <span className="opacity-90">({qtyData.final_safe_qty} Qty)</span>}
                    </span>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  className="text-xs text-gray-400 hover:text-white h-10 px-4 rounded-xl hover:bg-white/5"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Embedded Connect Dhan Modal in case broker is not connected */}
      <ConnectDhanModal
        open={connectModalOpen}
        onOpenChange={setConnectModalOpen}
        onConnected={() => {
          fetchStatusAndSecurity();
          if (entry && sl) calculateSafeQuantity();
        }}
      />
    </>
  );
}


export function DhanPortfolioWidget() {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initial fetch
    fetchData(true);

    // Set up polling every 3 seconds
    const interval = setInterval(() => {
      fetchData(false);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const fetchData = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      // Fetch status securely via new endpoints
      const statusRes = await axios.get(`${API}/trading/dhan-status`, { withCredentials: true }).catch(() => null);
      if (statusRes && statusRes.data) {
        setStatus(statusRes.data);
      }

      // Fetch portfolio summary securely via new endpoints
      const summaryRes = await axios.get(`${API}/trading/portfolio-summary`, { withCredentials: true }).catch(() => null);
      if (summaryRes && summaryRes.data && summaryRes.data.status === 'success') {
        setSummary(summaryRes.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-100 dark:bg-white/5 backdrop-blur-xl rounded-lg p-4 flex items-center justify-center h-24 mb-4 border border-gray-200 dark:border-white/10 animate-pulse">
        <div className="flex gap-2 items-center">
          <div className="w-4 h-4 rounded-full bg-white/20"></div>
          <div className="w-24 h-4 rounded bg-white/10"></div>
        </div>
      </div>
    );
  }

  if (!status?.connected) return null;

  const totalMtm = summary?.total_pnl || 0;
  const funds = summary?.funds || {};

  return (
    <div className="bg-gray-100 dark:bg-white/5 backdrop-blur-xl rounded-xl overflow-hidden mb-4 border border-gray-200 dark:border-white/10 shadow-2xl relative group hover:border-[#00FF88]/30 transition-all duration-500">
      <div className="absolute top-0 left-0 w-32 h-32 bg-[#00FF88]/5 rounded-full blur-3xl -ml-10 -mt-10 transition-all duration-500 group-hover:bg-[#00FF88]/10"></div>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-white/5 bg-gray-100 dark:bg-white/5 relative z-10">
        <Wallet className="w-4 h-4 text-[#00FF88]" />
        <span className="text-[10px] font-outfit text-gray-400 uppercase tracking-wider font-bold">Broker Integration</span>

        {status.expired ? (
          <span className="ml-2 text-[10px] text-red-500 bg-red-500/10 px-2 py-0.5 rounded font-bold shadow-[0_0_8px_rgba(239,68,68,0.3)]">Token Expired</span>
        ) : (
          <div className="ml-2 flex items-center gap-1 bg-[#00FF88]/10 px-2 py-0.5 rounded shadow-[0_0_8px_rgba(0,255,136,0.2)]">
            <div className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse shadow-[0_0_5px_#00FF88]"></div>
            <span className="text-[10px] text-[#00FF88] font-bold">LIVE</span>
          </div>
        )}

        <Button onClick={() => fetchData(true)} variant="ghost" size="icon" className="w-6 h-6 ml-auto hover:bg-white/10 text-gray-400">
          <RefreshCw className="w-3 h-3" />
        </Button>
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
        <div className="bg-gray-100 dark:bg-white/5 backdrop-blur-md p-4 rounded-lg border border-gray-200 dark:border-white/5">
          <p className="text-[10px] text-gray-500 font-outfit uppercase mb-1 font-bold">Status</p>
          <p className="text-sm font-bold font-mono text-gray-900 dark:text-white">
            {status.client_id}
          </p>
        </div>
        <div className="bg-gray-100 dark:bg-white/5 backdrop-blur-md p-4 rounded-lg border border-gray-200 dark:border-white/5 relative overflow-hidden">
          <p className="text-[10px] text-gray-500 font-outfit uppercase mb-1 font-bold">Total P&L (MTM)</p>
          <p className={`text-2xl font-bold font-mono drop-shadow-[0_0_10px_currentColor] ${totalMtm >= 0 ? 'text-[#00FF88]' : 'text-red-500'}`}>
            {totalMtm >= 0 ? '+' : ''}₹ {Math.abs(totalMtm).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-gray-100 dark:bg-white/5 backdrop-blur-md p-4 rounded-lg border border-gray-200 dark:border-white/5">
          <p className="text-[10px] text-gray-500 font-outfit uppercase mb-1 font-bold">Available Funds</p>
          <p className="text-2xl font-bold font-mono text-gray-900 dark:text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">
            ₹ {(funds.available || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PositionSizingCalc — In-modal interactive position sizing widget
// ─────────────────────────────────────────────────────────────────────────────
export function PositionSizingCalc({ entry, sl, target, capacityData }) {
  const defaultCapital = capacityData?.available_capital || 50000;
  const [calcCap, setCalcCap] = useState(defaultCapital);
  const [riskPct, setRiskPct] = useState(2);

  const numEntry = Number(entry) || 0;
  const numSl = Number(sl) || 0;
  const numTarget = Number(target) || 0;
  const pointRisk = Math.abs(numEntry - numSl);
  const pointReward = Math.abs(numTarget - numEntry);

  const maxRiskAmount = (calcCap * riskPct) / 100;
  const safeQty = pointRisk > 0 ? Math.floor(maxRiskAmount / pointRisk) : 0;
  const orderValue = safeQty * numEntry;
  const potentialLoss = safeQty * pointRisk;
  const potentialProfit = safeQty * pointReward;

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-2.5 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-[#00F0FF] font-bold uppercase">
          <Calculator className="w-3.5 h-3.5" /> Smart Position Sizing
        </div>
        <Link
          to="/calculator"
          className="text-[9px] font-mono text-[#00FF88] hover:underline flex items-center gap-0.5"
        >
          Full Calculator ↗
        </Link>
      </div>

      {/* Capital & Risk Inputs */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-1.5">
          <p className="text-[8px] uppercase text-gray-400 font-mono">Your Capital (₹)</p>
          <input
            type="number"
            value={calcCap}
            onChange={(e) => setCalcCap(Math.max(1000, Number(e.target.value)))}
            className="w-full bg-transparent text-xs font-mono font-bold text-white outline-none mt-0.5"
          />
        </div>
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-1.5">
          <p className="text-[8px] uppercase text-gray-400 font-mono">Risk %</p>
          <div className="flex items-center gap-1 mt-0.5">
            {[1, 2, 3].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRiskPct(r)}
                className={`text-[9px] px-1.5 py-0.5 rounded font-mono font-bold transition-all ${
                  riskPct === r
                    ? "bg-[#00FF88] text-black"
                    : "bg-white/[0.06] text-gray-400 hover:text-white"
                }`}
              >
                {r}%
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calculated quantities */}
      <div className="grid grid-cols-3 gap-1.5 text-center font-mono">
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-1.5">
          <p className="text-[8px] uppercase text-gray-400">Safe Qty</p>
          <p className="text-sm font-black text-[#00FF88] mt-0.5">{safeQty > 0 ? safeQty : "—"}</p>
        </div>
        <div className="bg-[#FF3B30]/[0.05] border border-[#FF3B30]/20 rounded-lg p-1.5">
          <p className="text-[8px] uppercase text-[#FF3B30]">Max Risk (SL)</p>
          <p className="text-xs font-bold text-[#FF3B30] mt-0.5">
            -₹{potentialLoss > 0 ? potentialLoss.toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—"}
          </p>
        </div>
        <div className="bg-[#00FF88]/[0.05] border border-[#00FF88]/20 rounded-lg p-1.5">
          <p className="text-[8px] uppercase text-[#00FF88]">Reward (Tgt)</p>
          <p className="text-xs font-bold text-[#00FF88] mt-0.5">
            +₹{potentialProfit > 0 ? potentialProfit.toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—"}
          </p>
        </div>
      </div>

      {/* Required Capital & R:R */}
      <div className="flex items-center justify-between text-[9px] font-mono px-1 text-gray-400 pt-0.5">
        <span>Capital Req: <strong className="text-white">₹{orderValue.toLocaleString("en-IN")}</strong></span>
        <span>R:R <strong className="text-[#00FF88]">1:{(pointRisk > 0 ? pointReward / pointRisk : 0).toFixed(2)}</strong></span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AlertInfoModal — Rich read-only trade setup panel for regular users
// Shows all trade details without auto-execution. Users trade manually on
// their own broker app. Backend order code is preserved for future use.
// ─────────────────────────────────────────────────────────────────────────────
export function AlertInfoModal({ open, onOpenChange, alert, livePrice: initialLivePrice, capacityData }) {
  const [liveData, setLiveData] = useState(initialLivePrice || null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [qtyData, setQtyData] = useState(null);
  const liveIntervalRef = useRef(null);

  const isDemand = alert?.zone_type === 'DEMAND';
  const accentColor = isDemand ? '#00FF88' : '#FF3B30';
  const accentBg = isDemand ? 'rgba(0,255,136,0.06)' : 'rgba(255,59,48,0.06)';

  const cmp = liveData?.last_price || 0;
  const entry = alert?.entry;
  const sl = alert?.stop_loss;
  const target = alert?.target;
  const isMarketOpen = liveData?.market_open;

  // R:R calculation
  const rr = (() => {
    if (!entry || !sl || !target) return '—';
    const risk = Math.abs(entry - sl);
    const reward = Math.abs(target - entry);
    return risk > 0 ? (reward / risk).toFixed(1) : '—';
  })();

  const distFromEntry = cmp && entry ? ((cmp - entry) / entry * 100).toFixed(2) : null;
  const distToTarget = cmp && target ? ((target - cmp) / cmp * 100).toFixed(2) : null;
  const distToSl = cmp && sl ? ((cmp - sl) / cmp * 100).toFixed(2) : null;

  // Progress bar position
  const progressPct = (() => {
    if (!cmp || !sl || !target) return 0;
    const low = isDemand ? sl : target;
    const high = isDemand ? target : sl;
    const range = high - low;
    if (range <= 0) return 50;
    return Math.max(0, Math.min(100, ((cmp - low) / range) * 100));
  })();

  const fetchLivePrice = useCallback(async () => {
    if (!alert?.script_name) return;
    setLiveLoading(true);
    try {
      const res = await axios.get(`${API}/market/ltp?symbols=${alert.script_name}`);
      const data = res.data?.data?.[alert.script_name];
      if (data) setLiveData(data);
    } catch (e) { /* silent */ }
    finally { setLiveLoading(false); }
  }, [alert?.script_name]);

  const calculateQty = useCallback(async () => {
    if (!entry || !sl) return;
    try {
      const res = await axios.post(`${API}/trading/calculate-qty`, {
        symbol: alert?.script_name,
        entry: parseFloat(entry),
        stop_loss: parseFloat(sl)
      }, { withCredentials: true });
      setQtyData(res.data);
    } catch (err) { /* silent — qty is informational */ }
  }, [alert?.script_name, entry, sl]);

  useEffect(() => {
    if (open) {
      setLiveData(initialLivePrice || null);
      setQtyData(null);
      fetchLivePrice();
      calculateQty();
      liveIntervalRef.current = setInterval(fetchLivePrice, 8000);
    } else {
      if (liveIntervalRef.current) clearInterval(liveIntervalRef.current);
    }
    return () => { if (liveIntervalRef.current) clearInterval(liveIntervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, alert]);

  if (!alert) return null;

  const brokerSteps = isDemand
    ? [
        `Open your broker app (Dhan / Zerodha / Upstox)`,
        `Search for "${alert.script_name}" and tap BUY`,
        `Set Order Type: LIMIT, Price: ₹${entry ? Number(entry).toLocaleString('en-IN') : '—'}`,
        `Set Stop Loss: ₹${sl ? Number(sl).toLocaleString('en-IN') : '—'}`,
        `Set Target / Take Profit: ₹${target ? Number(target).toLocaleString('en-IN') : '—'}`,
        `Use ${qtyData?.final_safe_qty || '—'} qty for 1% risk sizing (based on your capital)`,
      ]
    : [
        `Open your broker app (Dhan / Zerodha / Upstox)`,
        `Search for "${alert.script_name}" and tap SELL`,
        `Set Order Type: LIMIT, Price: ₹${entry ? Number(entry).toLocaleString('en-IN') : '—'}`,
        `Set Stop Loss: ₹${sl ? Number(sl).toLocaleString('en-IN') : '—'}`,
        `Set Target / Take Profit: ₹${target ? Number(target).toLocaleString('en-IN') : '—'}`,
        `Use ${qtyData?.final_safe_qty || '—'} qty for 1% risk sizing (based on your capital)`,
      ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px] w-[95vw] bg-[#0A0E17] border border-[#1E293B] text-white p-0 rounded-2xl overflow-hidden shadow-2xl">
        {/* Top Brand Accent */}
        <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${accentColor}, #00F0FF)` }} />

        {/* ── HEADER ───────────────────────────────────────────────────────── */}
        <div className="px-4 py-3 border-b border-white/[0.06]" style={{ background: accentBg }}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 font-outfit font-black text-lg sm:text-xl tracking-wide" style={{ color: accentColor }}>
                {isDemand ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                <span>{isDemand ? 'BUY' : 'SELL'} {alert.script_name}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] font-mono">
                {alert.setup_type && (
                  <span className="text-[#FFCC00] bg-[#FFCC00]/10 px-1.5 py-0.5 rounded font-semibold border border-[#FFCC00]/20">
                    {alert.setup_type}
                  </span>
                )}
                {alert.timeframe && (
                  <span className="text-gray-300 bg-white/5 px-1.5 py-0.5 rounded border border-white/10">
                    ⏱ {alert.timeframe}
                  </span>
                )}
                {alert.score && (
                  <span className="text-[#00FF88] bg-[#00FF88]/10 px-1.5 py-0.5 rounded border border-[#00FF88]/20 font-bold">
                    ★ {alert.score}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Coming Soon badge */}
              <span className="hidden sm:flex items-center gap-1 text-[9px] font-mono font-bold px-2 py-0.5 rounded-full bg-[#FFCC00]/10 text-[#FFCC00] border border-[#FFCC00]/20">
                <Zap className="w-2.5 h-2.5" /> Auto-Trade Soon
              </span>
              <button
                onClick={() => onOpenChange(false)}
                className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* CMP Live Row */}
          <div className="mt-2.5 pt-2 border-t border-white/[0.04] flex items-center justify-between flex-wrap gap-2 text-xs font-mono">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-gray-400">
                {isMarketOpen ? (
                  <><span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" /><span className="text-[#00FF88]">LTP</span></>
                ) : (
                  <><span className="w-1.5 h-1.5 rounded-full bg-gray-500" /><span>LTP</span></>
                )}
              </div>
              <span className="text-base sm:text-lg font-black text-white">
                ₹{cmp > 0 ? Number(cmp).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
              </span>
              {liveData?.change != null && (
                <span className={`text-[11px] font-bold ${liveData.change >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                  {liveData.change >= 0 ? '▲' : '▼'}{Math.abs(liveData.change).toFixed(2)} ({liveData.change_pct >= 0 ? '+' : ''}{liveData.change_pct}%)
                </span>
              )}
              <button onClick={fetchLivePrice} className="p-0.5 text-gray-400 hover:text-white transition-colors" title="Refresh price">
                <RefreshCw className={`w-3 h-3 ${liveLoading ? 'animate-spin text-[#00F0FF]' : ''}`} />
              </button>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-gray-400">
              <span>R:R <span className="font-bold text-[#00FF88]">1:{rr}</span></span>
            </div>
          </div>

          {/* Progress Track */}
          {cmp > 0 && sl && target && (
            <div className="mt-2 pt-1">
              <div className="relative h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                <div className="absolute left-0 top-0 h-full rounded-l-full bg-[#FF3B30]/50" style={{ width: isDemand ? '18%' : '82%' }} />
                <div className="absolute right-0 top-0 h-full rounded-r-full bg-[#00FF88]/50" style={{ width: '18%' }} />
                <div
                  className="absolute top-0 h-full w-1.5 rounded-full bg-white shadow-[0_0_6px_white] transition-all duration-300"
                  style={{ left: `${progressPct}%`, transform: 'translateX(-50%)' }}
                />
              </div>
              <div className="flex justify-between items-center mt-1 text-[9px] font-mono text-gray-400">
                <span className="text-[#FF3B30]">SL ₹{Number(isDemand ? sl : target).toLocaleString('en-IN')}</span>
                <span className="text-gray-300">CMP @ {progressPct.toFixed(0)}%</span>
                <span className="text-[#00FF88]">Target ₹{Number(isDemand ? target : sl).toLocaleString('en-IN')}</span>
              </div>
            </div>
          )}

          {/* ── TRADABILITY BANNER ──────────────────────────────────────────── */}
          {(() => {
            const isConnected = capacityData?.connected;
            const canAfford = qtyData != null && qtyData.final_safe_qty > 0;
            const notConnected = !isConnected && !qtyData;

            if (notConnected) return (
              <div className="mt-2.5 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08]">
                <Wallet className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="text-[10px] font-mono text-gray-400">
                  Connect your Dhan account to see if you can afford this setup
                </span>
              </div>
            );

            if (canAfford) return (
              <div className="mt-2.5 flex items-center justify-between px-3 py-2 rounded-lg bg-[#00FF88]/[0.08] border border-[#00FF88]/25">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-[#00FF88] shrink-0" />
                  <span className="text-[10px] font-mono font-bold text-[#00FF88]">
                    YOU CAN TRADE THIS
                  </span>
                </div>
                <span className="text-[10px] font-mono text-gray-400">
                  Safe qty: <span className="text-[#00FF88] font-bold">{qtyData.final_safe_qty}</span> shares
                  {qtyData.available_capital > 0 && (
                    <> · Capital: <span className="text-white">₹{Number(qtyData.available_capital).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></>
                  )}
                </span>
              </div>
            );

            // qty = 0 but connected
            return (
              <div className="mt-2.5 flex items-center justify-between px-3 py-2 rounded-lg bg-[#FF3B30]/[0.08] border border-[#FF3B30]/25">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-[#FF3B30] shrink-0" />
                  <span className="text-[10px] font-mono font-bold text-[#FF3B30]">
                    INSUFFICIENT CAPITAL
                  </span>
                </div>
                <span className="text-[10px] font-mono text-gray-400">
                  {qtyData?.available_capital > 0
                    ? `Available: ₹${Number(qtyData.available_capital).toLocaleString('en-IN', { maximumFractionDigits: 0 })} · Add more funds`
                    : 'No available funds in Dhan account'}
                </span>
              </div>
            );
          })()}
        </div>

        {/* ── BODY ─────────────────────────────────────────────────────────── */}
        <div className="p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto overscroll-contain touch-pan-y custom-scrollbar overscroll-contain touch-pan-y">

          {/* ── LEFT: Trade Levels ──────────────────────────────────────── */}
          <div className="space-y-2.5">
            {/* Price 3-grid */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1.5">Trade Levels</p>
              <div className="grid grid-cols-3 gap-1.5 text-center font-mono">
                <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-2 hover:border-white/20 transition-colors">
                  <p className="text-[9px] uppercase text-gray-400">Entry</p>
                  <p className="text-sm font-bold text-white mt-0.5">
                    ₹{entry ? Number(entry).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}
                  </p>
                  <p className="text-[8px] text-gray-400 mt-0.5">
                    {distFromEntry != null ? `${parseFloat(distFromEntry) >= 0 ? '+' : ''}${distFromEntry}%` : 'Limit'}
                  </p>
                </div>
                <div className="bg-[#FF3B30]/[0.05] border border-[#FF3B30]/20 rounded-xl p-2">
                  <p className="text-[9px] uppercase text-[#FF3B30]">Stop Loss</p>
                  <p className="text-sm font-bold text-[#FF3B30] mt-0.5">
                    ₹{sl ? Number(sl).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}
                  </p>
                  <p className="text-[8px] text-[#FF3B30]/80 mt-0.5">{distToSl != null ? `${distToSl}%` : '—'}</p>
                </div>
                <div className="bg-[#00FF88]/[0.05] border border-[#00FF88]/20 rounded-xl p-2">
                  <p className="text-[9px] uppercase text-[#00FF88]">Target</p>
                  <p className="text-sm font-bold text-[#00FF88] mt-0.5">
                    ₹{target ? Number(target).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}
                  </p>
                  <p className="text-[8px] text-[#00FF88]/80 mt-0.5">{distToTarget != null ? `+${distToTarget}%` : '—'}</p>
                </div>
              </div>
            </div>

            {/* Smart Position Sizing Calculator */}
            <PositionSizingCalc entry={entry} sl={sl} target={target} capacityData={capacityData} />
          </div>

          {/* ── RIGHT: Manual Steps + Admin Insight + Teaser ────────────── */}
          <div className="space-y-2.5">
            {/* Admin remark */}
            {alert.admin_remark && (
              <div className="p-2.5 bg-[#FFCC00]/[0.05] border border-[#FFCC00]/15 rounded-xl flex items-start gap-1.5">
                <Shield className="w-3.5 h-3.5 text-[#FFCC00] shrink-0 mt-0.5" />
                <div className="text-[10px] font-sans text-[#CBD5E1] leading-tight">
                  <span className="text-[#FFCC00] font-bold font-mono uppercase mr-1">Admin Insight:</span>
                  {alert.admin_remark}
                </div>
              </div>
            )}

            {/* How to trade manually */}
            <div className="bg-white/[0.02] border border-[#00F0FF]/15 rounded-xl p-2.5">
              <div className="flex items-center gap-1.5 mb-2">
                <BarChart3 className="w-3.5 h-3.5 text-[#00F0FF]" />
                <span className="text-[10px] font-mono font-bold text-[#00F0FF] uppercase tracking-wider">
                  How to Place This Trade
                </span>
              </div>
              <ol className="space-y-1.5">
                {brokerSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2 text-[10px] text-gray-300 font-sans leading-tight">
                    <span
                      className="shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold font-mono mt-0.5"
                      style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
                    >
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>

            {/* Coming Soon Teaser */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#FFCC00]/[0.06] to-[#00F0FF]/[0.03] border border-[#FFCC00]/20 rounded-xl p-3">
              <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full bg-[#FFCC00]/10 blur-2xl" />
              <div className="relative flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#FFCC00]/10 border border-[#FFCC00]/20 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4 text-[#FFCC00]" />
                </div>
                <div>
                  <p className="text-[10px] font-bold font-mono text-[#FFCC00] uppercase tracking-wider">1-Click Auto-Trade — Coming Soon</p>
                  <p className="text-[9px] text-gray-400 font-sans mt-0.5 leading-snug">
                    We're integrating the partner API. Once live, you'll execute this trade with one tap — directly from here.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── FOOTER ───────────────────────────────────────────────────────── */}
        <div className="p-3 border-t border-white/[0.06] bg-[#070A10] flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-mono">
            <Activity className="w-3 h-3 text-[#00F0FF]" />
            <span>Prices auto-refresh every 8s</span>
          </div>
          <Button
            onClick={() => onOpenChange(false)}
            className="h-9 px-6 font-bold font-outfit text-sm rounded-xl"
            style={{ background: accentColor, color: isDemand ? '#000' : '#fff' }}
          >
            Got it, I'll trade manually
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
